import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import './TablaPrimerasMesadas.css';
import { fetchCausanteOrBeneficiario } from '../redux/causantesSlice';

const PerdidaAdquisitivos = () => {
  // Estado y selectores existentes se mantienen
  const dispatch = useDispatch();
  const facturas = useSelector((state) => state.pensiones.pensiones) || [];
  const { usuarioSeleccionado } = useSelector((state) => state.pensiones);
  const { userType, causanteData, status: causanteStatus } = useSelector((state) => state.causantes);
  const [mesadasAnuales, setMesadasAnuales] = useState([]);
  const [fechaCertificado, setFechaCertificado] = useState('');
  const [fechaInicioPension, setFechaInicioPension] = useState(null);
  const [registrosISS, setRegistrosISS] = useState([]);
  const printRef = useRef();
  
  // Datos de SMLMV por año, agregando 1999 y 2000
  const smlmvPorAnio = {
    1999: 236460, // Valor del salario mínimo de 1999
    2000: 260100, // Valor del salario mínimo de 2000
    2001: 286000,
    2002: 309000,
    2003: 332000,
    2004: 358000,
    2005: 381500,
    2006: 408000,
    2007: 433700,
    2008: 461500,
    2009: 496900,
    2010: 515000,
    2011: 535600,
    2012: 566000,
    2013: 589500,
    2014: 616000,
    2015: 644350,
    2016: 689455,
    2017: 737717,
    2018: 781242,
    2019: 828116,
    2020: 877803,
    2021: 908526,
    2022: 1000000,
    2023: 1160000,
    2024: 1300000,
    2025: 1423000
  };
  
  // Datos de IPC por año
  const ipcPorAnio = {
    1999: 9.23,
    2000: 8.75,
    2001: 7.65,
    2002: 6.99,
    2003: 6.49,
    2004: 5.50,
    2005: 4.85,
    2006: 4.48,
    2007: 5.69,
    2008: 7.67,
    2009: 2.00,
    2010: 3.17,
    2011: 3.73,
    2012: 2.44,
    2013: 1.94,
    2014: 3.66,
    2015: 6.77,
    2016: 5.75,
    2017: 4.09,
    2018: 3.18,
    2019: 3.80,
    2020: 1.61,
    2021: 5.62,
    2022: 13.12,
    2023: 9.28,
    2024: 4.30,
     2025: 5.20
  };

  // Cargar datos del causante cuando el componente se monta
  useEffect(() => {
    if (usuarioSeleccionado?.documento) {
      dispatch(fetchCausanteOrBeneficiario(usuarioSeleccionado.documento));
    }
  }, [dispatch, usuarioSeleccionado]);

  // Extraer registros de ISS cuando los datos del causante están disponibles
  useEffect(() => {
    if (causanteData && (userType === 'CAUSANTE' || userType === 'BENEFICIARIO') && Array.isArray(causanteData.records)) {
      // Filtrar todos los registros que tengan valor_iss, sin importar el tipo_aum
      const registrosConValorISS = causanteData.records
        .filter(record => record.valor_iss !== undefined && record.valor_iss !== null)
        .map(record => {
          // Convertir fecha_desde a objeto Date
          let fechaDesde = null;
          if (record.fecha_desde && record.fecha_desde.seconds) {
            fechaDesde = new Date(record.fecha_desde.seconds * 1000);
          } else if (typeof record.fecha_desde === 'string') {
            fechaDesde = new Date(record.fecha_desde);
          }

          return {
            año: fechaDesde ? fechaDesde.getFullYear() : null,
            valorISS: record.valor_iss || 0,
            valorEmpresa: record.valor_empresa || 0,
            tipoAum: record.tipo_aum || "",
            observacion: record.observacion || "",
            fechaDesde: fechaDesde,
            fechaHasta: record.fecha_hasta && record.fecha_hasta.seconds ? 
                        new Date(record.fecha_hasta.seconds * 1000) : null
          };
        })
        .filter(record => record.año !== null) // Solo registros con año válido
        .sort((a, b) => a.año - b.año); // Ordenar por año

      setRegistrosISS(registrosConValorISS);
      
      console.log("Registros procesados:", registrosConValorISS);
    }
  }, [causanteData, userType]);

  // El resto del useEffect para obtenerMesadas se mantiene
  useEffect(() => {
    const obtenerMesadas = () => {
      const facturasPorAño = {};

      const facturasOrdenadas = [...facturas].sort((a, b) => {
        const fechaA = new Date(
          `${a.año}-${obtenerMesDePeriodoPago(a.periodoPago).mesNumero}-01`
        );
        const fechaB = new Date(
          `${b.año}-${obtenerMesDePeriodoPago(b.periodoPago).mesNumero}-01`
        );
        return fechaA - fechaB;
      });

      const primeraFactura = facturasOrdenadas.find((factura) =>
        factura.detalles.some((detalle) => detalle.nombre === 'Mesada Pensional')
      );

      if (primeraFactura) {
        const { mesNombre } = obtenerMesDePeriodoPago(primeraFactura.periodoPago);
        setFechaInicioPension({
          año: primeraFactura.año,
          mes: mesNombre,
        });
      }

      facturasOrdenadas.forEach((factura) => {
        const year = factura.año;
        const { mesNumero } = obtenerMesDePeriodoPago(factura.periodoPago);
        const mesadaPensional = factura.detalles.find(
          (detalle) => detalle.nombre === 'Mesada Pensional'
        )?.ingresos || null;

        if (mesadaPensional !== null) {
          if (!facturasPorAño[year]) {
            facturasPorAño[year] = [];
          }
          facturasPorAño[year].push({
            mesNumero: parseInt(mesNumero, 10),
            valor: mesadaPensional,
          });
        }
      });

      const resultadoBase = Object.keys(facturasPorAño)
        .map((year) => {
          const pagosDelAño = facturasPorAño[year].sort((a, b) => a.mesNumero - b.mesNumero);
          const eneroValor = pagosDelAño[0]?.valor || null;
          const diciembreValor = pagosDelAño[pagosDelAño.length - 1]?.valor || null;

          let variacionPesos = null;
          let variacionPorcentaje = null;
          if (eneroValor !== null && diciembreValor !== null && eneroValor !== 0) {
            variacionPesos = diciembreValor - eneroValor;
            variacionPorcentaje = ((variacionPesos / eneroValor) * 100).toFixed(2);
          }

          return {
            año: parseInt(year, 10),
            enero: eneroValor,
            diciembre: diciembreValor,
            variacionPesos,
            variacionPorcentaje,
          };
        })
        .sort((a, b) => a.año - b.año);

      // Asegurarse de que 1999 y 2000 estén incluidos
      const años = resultadoBase.map(item => item.año);
      if (!años.includes(1999)) {
        resultadoBase.unshift({
          año: 1999,
          enero: null,
          diciembre: null,
          variacionPesos: null,
          variacionPorcentaje: null
        });
      }
      if (!años.includes(2000)) {
        // Encontrar la posición correcta para insertar el año 2000
        const index = resultadoBase.findIndex(item => item.año > 2000);
        const posicion = index === -1 ? resultadoBase.length : index;
        resultadoBase.splice(posicion, 0, {
          año: 2000,
          enero: null,
          diciembre: null,
          variacionPesos: null,
          variacionPorcentaje: null
        });
      }

      // Reordenar para asegurar que siguen en orden
      resultadoBase.sort((a, b) => a.año - b.año);

      // Crear una proyección de valores ISS basados en IPC para años sin datos reales
      let valoresISSproyectados = {};
      let añosConDatos = [];
      
      // Primero identificamos los valores ISS reales de los registros
      registrosISS.forEach(registro => {
        valoresISSproyectados[registro.año] = {
          valor: registro.valorISS,
          esReal: true
        };
        añosConDatos.push(registro.año);
      });
      
      // Ordenamos los años para facilitar la proyección
      añosConDatos.sort((a, b) => a - b);
      
      // Encontrar el primer año con datos ISS
      const primerAñoConDatos = añosConDatos.length > 0 ? Math.min(...añosConDatos) : null;
      
      // Obtenemos el rango completo de años a considerar
      const añosAConsiderar = resultadoBase.map(item => item.año).sort((a, b) => a - b);
      
      // SOLO proyectamos para años posteriores al primer año con datos
      if (primerAñoConDatos !== null) {
        // Para cada año que no tenga datos y sea POSTERIOR al primer año con datos
        for (let i = 0; i < añosAConsiderar.length; i++) {
          const añoActual = añosAConsiderar[i];
          
          // Solo proyectamos años posteriores al primer año con datos y que no tengan valores reales
          if (añoActual > primerAñoConDatos && !valoresISSproyectados[añoActual]) {
            // Caso especial para 2025: aplicar directamente el 5.20% al valor de 2024
            if (añoActual === 2025 && valoresISSproyectados[2024]) {
              valoresISSproyectados[2025] = {
                valor: Math.round(valoresISSproyectados[2024].valor * (1 + 5.20 / 100)),
                esReal: false
              };
            } else {
              // Encontrar el año anterior con datos (real o proyectado)
              let añoAnterior = añoActual - 1;
              while (añoAnterior >= primerAñoConDatos && !valoresISSproyectados[añoAnterior]) {
                añoAnterior--;
              }
              
              if (añoAnterior >= primerAñoConDatos && valoresISSproyectados[añoAnterior]) {
                let valorAnterior = valoresISSproyectados[añoAnterior].valor;
                let valorProyectado = valorAnterior;
                
                // Aplicar IPC para cada año desde el anterior hasta el actual
                for (let año = añoAnterior; año < añoActual; año++) {
                  valorProyectado *= (1 + (ipcPorAnio[año] || 0) / 100);
                }
                
                valoresISSproyectados[añoActual] = {
                  valor: Math.round(valorProyectado),
                  esReal: false
                };
              }
            }
          }
        }
      }

      // Modificar el resultado para incluir SMLMV y pensión de vejez
      const resultadoConComparacionAnterior = resultadoBase.map((item, index) => {
        const anterior = resultadoBase[index - 1];
        const smlmvActual = smlmvPorAnio[item.año] || null;
        
        // Para años anteriores al primer año con datos ISS, establecer valor en 0
        // Para el resto, usar el valor proyectado o real
        let pensionVejez = 0;
        if (primerAñoConDatos === null || item.año < primerAñoConDatos) {
          pensionVejez = 0; // Valor cero para años anteriores al primer año con datos
        } else {
          pensionVejez = valoresISSproyectados[item.año]?.valor || 0;
        }
        
        // Calcular la unidad pensional (suma de pagado por empresa + pensión de vejez)
        const unidadPensional = (item.enero || 0) + pensionVejez;
        
        // Calcular cuántos SMLMV representa la unidad pensional
        const numeroSMLMV = smlmvActual ? (unidadPensional / smlmvActual) : 0;
        
        return {
          ...item,
          smlmv: smlmvActual,
          pensionVejez,
          unidadPensional,
          numeroSMLMV,
          esValorISSReal: valoresISSproyectados[item.año]?.esReal || false,
        };
      });

      setMesadasAnuales(resultadoConComparacionAnterior);
    };

    const obtenerFechaActual = () => {
      const fecha = new Date();
      const opciones = { year: 'numeric', month: 'long', day: 'numeric' };
      const fechaFormateada = fecha.toLocaleDateString('es-CO', opciones);
      setFechaCertificado(fechaFormateada);
    };

    obtenerMesadas();
    obtenerFechaActual();
  }, [facturas, registrosISS, ipcPorAnio]); // Agregamos ipcPorAnio como dependencia

  const obtenerMesDePeriodoPago = (periodoPago) => {
    if (!periodoPago || typeof periodoPago !== 'string') {
      return { mesNumero: '', mesNombre: '' };
    }

    const meses = {
      ene: '01',
      feb: '02',
      mar: '03',
      abr: '04',
      may: '05',
      jun: '06',
      jul: '07',
      ago: '08',
      sep: '09',
      sept: '09',
      oct: '10',
      nov: '11',
      dic: '12',
    };
    const nombresMeses = {
      ene: 'Enero',
      feb: 'Febrero',
      mar: 'Marzo',
      abr: 'Abril',
      may: 'Mayo',
      jun: 'Junio',
      jul: 'Julio',
      ago: 'Agosto',
      sep: 'Septiembre',
      sept: 'Septiembre',
      oct: 'Octubre',
      nov: 'Noviembre',
      dic: 'Diciembre',
    };

    const regex = /([a-z]{3,4})\.?/gi;
    const matches = periodoPago.toLowerCase().match(regex);

    if (matches && matches.length > 0) {
      const mesAbreviado = matches[0].replace('.', '').replace(/[^a-z]/g, '');
      return { mesNumero: meses[mesAbreviado], mesNombre: nombresMeses[mesAbreviado] };
    }

    return { mesNumero: '', mesNombre: '' };
  };

  const handleGeneratePDF = async () => {
    const element = printRef.current;
    if (element) {
      const canvas = await html2canvas(element, { scale: 1, useCORS: true });
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginLeft = 0;
      const marginTop = 0;
      const usableWidth = pageWidth - marginLeft * 2;
      const usableHeight = pageHeight - marginTop * 2;

      let imgWidth = usableWidth;
      let imgHeight = (canvas.height * imgWidth) / canvas.width;

      if (imgHeight > usableHeight) {
        const scaleFactor = usableHeight / imgHeight;
        imgWidth *= scaleFactor;
        imgHeight *= scaleFactor;
      }

      const centeredX = (pageWidth - imgWidth) / 2;

      pdf.addImage(imgData, 'PNG', centeredX, marginTop, imgWidth, imgHeight);
      pdf.save('certificado_mesadas_pensionales.pdf');
    }
  };

  const formatoMoneda = (valor) => {
    if (valor == null || valor === '') return '$ 0';
    const numero = parseFloat(valor.toString().replace(/[^0-9.-]+/g, '').replace(',', '.'));
    if (isNaN(numero)) return '$ 0';
    return numero.toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
    });
  };

  // Encontrar la fecha más antigua de compartición ISS para mostrar en el certificado
  const fechaComparticionISS = React.useMemo(() => {
    if (registrosISS.length > 0) {
      const registroISS = registrosISS.find(reg => 
        reg.tipoAum === "ISS" || reg.observacion?.includes("Compartido con el ISS")
      );
      
      if (registroISS && registroISS.fechaDesde) {
        const fecha = registroISS.fechaDesde;
        return {
          dia: fecha.getDate(),
          mes: fecha.toLocaleString('es-CO', { month: 'long' }),
          año: fecha.getFullYear()
        };
      }
    }
    return null;
  }, [registrosISS]);

  return (
    <div className="tabla-primeras-mesadas-contenedor">
      <button className="boton-descargar" onClick={handleGeneratePDF}>Descargar PDF</button>
      <div ref={printRef} className="tabla-primeras-mesadas">
        <h1 className="certificado-titulo">Análisis del avance pensional</h1>
        {usuarioSeleccionado ? (
          <h3 className="certificado-subtitulo">{usuarioSeleccionado.nombre}</h3>
        ) : (
          <h3 className="certificado-subtitulo">Pensionado</h3>
        )}

        <table>
          <thead>
            <tr>
              <th>Año</th>
              <th>SMLMV</th>
              <th>Pagado por la empresa</th>
              <th>Pension de vejez</th>
              <th>Unidad Pensional</th>
              <th>#SMLMV</th>
            </tr>
          </thead>
          <tbody>
            {mesadasAnuales.map((item, index) => (
              <tr key={item.año} className={index % 2 === 0 ? "fila-par" : "fila-impar"}>
                <td>{item.año}</td>
                <td>{formatoMoneda(item.smlmv)}</td>
                <td>{formatoMoneda(item.enero)}</td>
                <td>
                  {formatoMoneda(item.pensionVejez)}
                  {!item.esValorISSReal && item.pensionVejez > 0 && ' *'}
                </td>
                <td>{formatoMoneda(item.unidadPensional)}</td>
                <td>{item.numeroSMLMV ? item.numeroSMLMV.toFixed(2) : '-'}</td>
              </tr>
            ))}
            
            {/* La fila de totales debe mantener su estilo original */}
            {mesadasAnuales.length > 1 && (
              <tr className="fila-totales">
                <td colSpan="5" style={{textAlign: 'right', fontWeight: 'bold'}}>
                  PÉRDIDA DE SALARIOS MÍNIMOS EN EL PERIODO:
                </td>
                <td style={{fontWeight: 'bold'}}>
                  {mesadasAnuales.length > 0 ? 
                    (mesadasAnuales[0].numeroSMLMV - mesadasAnuales[mesadasAnuales.length - 1].numeroSMLMV).toFixed(2) : 
                    '-'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        
        {!mesadasAnuales.every(item => item.esValorISSReal) && (
          <p className="tabla-nota"><small>* Valores proyectados basados en el IPC anual</small></p>
        )}
        
        {/* Reemplazar el párrafo de explicación por una tabla de compartición */}
        {/* Tabla de compartición con formato mejorado */}
        <div className="comparticion-tabla">
          <table className="tabla-comparticion">
            <tbody>
              <tr>
                <td className="titulo-comparticion">FECHA DE COMPARTICIÓN DE LA PENSIÓN CONVENCIONAL CON LA PENSIÓN DEL ISS - COLPENSIONES</td>
                <td className="valor-comparticion">
                  {fechaComparticionISS ? 
                    `${fechaComparticionISS.dia}/${fechaComparticionISS.mes.substring(0,3).toLowerCase()}/${fechaComparticionISS.año}` : 
                    "00/00/0000"}
                </td>
              </tr>
              <tr>
                <td className="titulo-comparticion">MESADA PLENA DE LA PENSIÓN CONVENCIONAL ANTES DE LA COMPARTICIÓN</td>
                <td className="valor-comparticion">
                  {registrosISS.length > 0 && registrosISS[0] ? formatoMoneda(registrosISS[0].valorEmpresa) : "$ 0"}
                </td>
              </tr>
            </tbody>
          </table>

          <table className="tabla-comparticion tabla-secundaria">
            <tbody>
              <tr>
                <td className="titulo-comparticion-header" colSpan="3">PORCENTAJE Y VALORES DE CUOTAS PARTES EN QUE SE DISTRIBUYE EL MONTO DE LA MESADA PENSIONAL A PARTIR DE LA COMPARTICIÓN</td>
              </tr>
              {registrosISS.length > 0 && registrosISS[registrosISS.length-1] && (
                <>
                  <tr>
                    <td className="concepto-comparticion">A CARGO DE COLPENSIONES</td>
                    <td className="valor-porcentaje">
                      {((registrosISS[registrosISS.length-1].valorISS / 
                        (registrosISS[registrosISS.length-1].valorISS + registrosISS[registrosISS.length-1].valorEmpresa)) * 100).toFixed(2)} %
                    </td>
                    <td className="valor-monto">
                      {formatoMoneda(registrosISS[registrosISS.length-1].valorISS)}
                    </td>
                  </tr>
                  <tr>
                    <td className="concepto-comparticion">MAYOR VALOR A CARGO DE LA EMPRESA</td>
                    <td className="valor-porcentaje">
                      {((registrosISS[registrosISS.length-1].valorEmpresa / 
                        (registrosISS[registrosISS.length-1].valorISS + registrosISS[registrosISS.length-1].valorEmpresa)) * 100).toFixed(2)} %
                    </td>
                    <td className="valor-monto">
                      {formatoMoneda(registrosISS[registrosISS.length-1].valorEmpresa)}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <p className="certificado-nota">
            Este certificado se expide a solicitud de la parte interesada para los fines legales y administrativos a los que haya lugar.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PerdidaAdquisitivos;
